import type { ChatThread, ProviderChatThread } from "./types";
import { isVirtualThreadId } from "./virtual-thread";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useRouter, useSearch } from "@tanstack/react-router";
import { format, isToday, isYesterday } from "date-fns";
import { useEffect, useRef, memo } from "react";
import {
  MessageCircle,
  MessageSquarePlus,
  Building2,
  Briefcase,
  Clock,
  EyeOff,
  FileText,
  ListFilter,
  Search,
  X,
  UserCircle,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

function getEmptyStateCopy({
  searchQuery,
  unreadOnly,
  isPoolRestricted,
}: {
  searchQuery: string;
  unreadOnly: boolean;
  isPoolRestricted: boolean;
}) {
  if (searchQuery) {
    return {
      title: "No matches found",
      description: "Try a different search term",
    };
  }

  if (unreadOnly) {
    return {
      title: "No unread conversations",
      description: "You're all caught up",
    };
  }

  return {
    title: "No conversations yet",
    description: isPoolRestricted
      ? "No providers in your assigned pools have activity yet."
      : "Start a conversation to see it here",
  };
}

const ChatThreadListComponent: React.FC<{
  threads: ChatThread[];
  onSelectThread: (thread: ChatThread) => void;
  selectedThread: ChatThread | null;
  user:
    | {
        id: string;
        is_admin?: boolean;
      }
    | undefined;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  assignedToMe?: boolean;
  onAssignedToMeChange?: (value: boolean) => void;
  unreadOnly?: boolean;
  onUnreadOnlyChange?: (value: boolean) => void;
  /** When true, the empty state copy reflects pool-filtering rather than a generic "no chats" message. */
  isPoolRestricted?: boolean;
}> = ({
  threads,
  onSelectThread,
  selectedThread,
  user,
  onLoadMore,
  hasMore,
  isLoading,
  searchQuery = "",
  onSearchChange,
  assignedToMe,
  onAssignedToMeChange,
  unreadOnly = false,
  onUnreadOnlyChange,
  isPoolRestricted = false,
}) => {
  const search = useSearch({
    strict: false,
  });
  const router = useRouter();
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const emptyStateCopy = getEmptyStateCopy({
    searchQuery,
    unreadOnly,
    isPoolRestricted,
  });

  // Infinite scroll logic
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !isLoading && onLoadMore) {
          onLoadMore();
        }
      },
      { threshold: 0.1 },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, isLoading, onLoadMore]);

  const currentView = search.archived
    ? "archived"
    : assignedToMe
      ? "mine"
      : "all";

  const handleViewChange = (view: "mine" | "all" | "archived") => {
    router.navigate({
      // @ts-expect-error - existing dynamic route typing
      search: (prev) => ({
        ...prev,
        archived: view === "archived" || undefined,
        assignedToMe: view === "mine" || undefined,
      }),
    });
    onAssignedToMeChange?.(view === "mine");
  };

  // No need for client-side filtering since search is handled server-side
  const filteredThreads = threads;

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) {
      return "";
    }

    const date = new Date(timestamp);
    const now = new Date();

    if (isToday(date)) {
      return format(date, "h:mm a");
    } else if (isYesterday(date)) {
      return "Yesterday";
    } else if (now.getTime() - date.getTime() < 7 * 24 * 60 * 60 * 1000) {
      return format(date, "EEE");
    } else {
      return format(date, "MMM d");
    }
  };

  const getThreadTitle = (thread: ProviderChatThread) => {
    if (thread.user_participants.length > 0) {
      return thread.user_participants
        .map(
          (p: ProviderChatThread["user_participants"][number]) =>
            `${p.first_name} ${p.last_name}`,
        )
        .join(", ");
    }
    return thread.organization_participants
      .map(
        (p: ProviderChatThread["organization_participants"][number]) => p.name,
      )
      .join(", ");
  };

  const getLastMessagePreview = (thread: ProviderChatThread) => {
    if (isVirtualThreadId(thread.id)) {
      return { text: "Send the first message", icon: MessageSquarePlus };
    }

    if (!thread.last_message) {
      return { text: "Start a conversation", icon: MessageCircle };
    }

    const isFromMe = thread.last_message_sender_user_id === user?.id;

    if (thread.last_message.type === "job-offer") {
      return {
        text: isFromMe ? "You: Job offer sent" : "Job offer received",
        icon: Briefcase,
      };
    }

    if (thread.last_message.type === "job-offer-proposal") {
      return {
        text: isFromMe ? "You: Changes proposed" : "Changes proposed",
        icon: Briefcase,
      };
    }

    if (thread.last_message.type === "job-offer-shifts-canceled") {
      const count =
        (thread.last_message as { shiftCount?: number }).shiftCount ?? 0;
      return {
        text: isFromMe
          ? `You: ${count} shift${count !== 1 ? "s" : ""} canceled`
          : `${count} shift${count !== 1 ? "s" : ""} canceled`,
        icon: Briefcase,
      };
    }

    if (thread.last_message.type === "credential-edit-request") {
      return {
        text: isFromMe
          ? "You: Credential edit access requested"
          : "Credential edit access requested",
        icon: Briefcase,
      };
    }

    if (thread.last_message.type === "form-assignment") {
      return {
        text: isFromMe ? "You: Sent a form" : "Form received",
        icon: FileText,
      };
    }

    if (
      thread.last_message.type === "chat" &&
      thread.last_message.attachments?.length &&
      !thread.last_message.text
    ) {
      const count = thread.last_message.attachments.length;
      const fileText = count === 1 ? "Sent a file" : `Sent ${count} files`;
      return {
        text: isFromMe ? `You: ${fileText}` : fileText,
        icon: MessageCircle,
      };
    }

    const text = thread.last_message.text;
    const displayText =
      text && text.length > 50 ? `${text.slice(0, 50)}...` : text || "Message";

    return {
      text: isFromMe ? `You: ${displayText}` : displayText,
      icon: MessageCircle,
    };
  };

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-white">
      {/* Conversation filters */}
      <div className="shrink-0 border-b border-slate-200 bg-white p-3 sm:p-4">
        {onAssignedToMeChange ? (
          <div className="flex h-10 items-center rounded-lg border border-slate-200 bg-slate-100 p-0.5">
            {(
              [
                { key: "mine", label: "My Chats" },
                { key: "all", label: "All Chats" },
                { key: "archived", label: "Archived" },
              ] as const
            ).map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => handleViewChange(key)}
                aria-pressed={currentView === key}
                className={`h-8 min-w-0 flex-1 rounded-md px-2 py-1 text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                  currentView === key
                    ? "bg-primary text-primary-foreground shadow-xs"
                    : "text-slate-600 hover:bg-white hover:text-slate-900"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        ) : (
          <Tabs
            defaultValue={search.archived ? "archived" : "active"}
            onValueChange={(value) => {
              router.navigate({
                // @ts-expect-error - existing dynamic route typing
                search: (prev) => ({
                  ...prev,
                  archived: value === "archived",
                }),
              });
            }}
          >
            <TabsList className="grid h-10 grid-cols-2 rounded-lg border border-slate-200 bg-slate-100 p-0.5">
              <TabsTrigger
                value="active"
                className="h-8 min-w-0 rounded-md px-3 py-1 text-sm font-medium text-slate-600 focus-visible:ring-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-xs data-[state=inactive]:hover:bg-white data-[state=inactive]:hover:text-slate-900"
              >
                Active
              </TabsTrigger>
              <TabsTrigger
                value="archived"
                className="h-8 min-w-0 rounded-md px-3 py-1 text-sm font-medium text-slate-600 focus-visible:ring-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-xs data-[state=inactive]:hover:bg-white data-[state=inactive]:hover:text-slate-900"
              >
                Archived
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        {/* Search and secondary filters */}
        <div className="mt-3 flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input
              key="search-input"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => onSearchChange?.(e.target.value)}
              className="pl-10 pr-12"
              aria-label="Search conversations"
              autoComplete="off"
            />
            {searchQuery && (
              <button
                type="button"
                aria-label="Clear conversation search"
                onClick={() => onSearchChange?.("")}
                className="absolute right-0 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-xl text-slate-500 hover:text-slate-700 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <button
            type="button"
            aria-label="Show unread conversations only"
            aria-pressed={unreadOnly}
            title="Show unread conversations only"
            onClick={() => onUnreadOnlyChange?.(!unreadOnly)}
            className={`inline-flex h-12 min-w-12 shrink-0 items-center justify-center gap-2 rounded-xl border px-3 text-sm font-medium focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
              unreadOnly
                ? "border-primary/20 bg-primary/5 text-primary hover:bg-primary/10"
                : "border-slate-400 bg-white text-slate-600 hover:border-slate-500 hover:bg-slate-50"
            }`}
          >
            <ListFilter className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Unread</span>
          </button>
        </div>
      </div>

      {/* Conversations */}
      <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto bg-white">
        {!isLoading && filteredThreads.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center px-6">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
              <Search className="h-5 w-5 text-slate-400" />
            </div>
            <p className="text-sm font-medium text-slate-700">
              {emptyStateCopy.title}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {emptyStateCopy.description}
            </p>
          </div>
        )}
        {filteredThreads.map((thread) => {
          const isSelected = selectedThread?.id === thread.id;
          const lastMessage = getLastMessagePreview(thread);
          const MessageIcon = lastMessage.icon;

          return (
            <button
              key={thread.id}
              type="button"
              aria-pressed={isSelected}
              className={`group relative block w-full min-w-0 border-b border-l-4 border-slate-200 px-4 py-4 text-left transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${isSelected ? "border-l-primary bg-primary/5" : "border-l-transparent bg-white hover:bg-slate-50"}`}
              onClick={() => onSelectThread(thread)}
            >
              <div className="flex min-w-0 items-start gap-3">
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  <Avatar className="h-10 w-10 rounded-xl border border-slate-200">
                    <AvatarFallback
                      className={`rounded-xl text-sm font-semibold ${isSelected ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-600"}`}
                    >
                      {thread.organization_participants.length > 0
                        ? thread.organization_participants[0]?.name?.[0]?.toUpperCase()
                        : thread.user_participants[0]?.first_name?.[0]?.toUpperCase() ||
                          "?"}
                    </AvatarFallback>
                  </Avatar>
                </div>

                {/* Content Area */}
                <div className="flex-1 min-w-0 space-y-1">
                  {/* Header Row */}
                  <div className="flex min-w-0 items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-1 items-center gap-1.5">
                      <h3
                        title={getThreadTitle(thread)}
                        className={`
                        min-w-0 truncate font-semibold text-base leading-6
                        ${isSelected ? "text-primary" : "text-slate-900"}
                      `}
                      >
                        {getThreadTitle(thread)}
                      </h3>

                      {thread.organization_participants.length > 0 && (
                        <Building2
                          className={`
                          w-4 h-4 flex-shrink-0
                          ${isSelected ? "text-primary" : "text-slate-400"}
                        `}
                        />
                      )}
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      {/* Timestamp */}
                      <div
                        className={`
                        text-xs font-medium flex shrink-0 items-center gap-1 whitespace-nowrap
                        ${
                          isSelected
                            ? "text-primary font-semibold"
                            : "text-slate-500 group-hover:text-slate-600"
                        }
                      `}
                      >
                        <Clock className="h-3 w-3 shrink-0" />
                        <span>{formatTimestamp(thread.last_message_time)}</span>
                      </div>

                      {/* Read Status Indicator */}
                      {thread.user_participants.length > 0 && (
                        <div className="flex items-center">
                          {thread.user_participants[0]
                            ?.has_read_latest_message ? null : (
                            <Popover>
                              <PopoverTrigger asChild>
                                <div
                                  role="button"
                                  tabIndex={0}
                                  aria-label="Message read status"
                                  className="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-md hover:bg-slate-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }
                                  }}
                                >
                                  <EyeOff
                                    className={`
                                      w-4 h-4
                                      ${
                                        isSelected
                                          ? "text-amber-600"
                                          : "text-amber-500 group-hover:text-amber-600"
                                      }
                                    `}
                                  />
                                </div>
                              </PopoverTrigger>
                              <PopoverContent
                                className="w-64 max-w-[calc(100vw-2rem)] rounded-xl border-slate-200 bg-white p-4 text-slate-700"
                                side="left"
                                collisionPadding={16}
                              >
                                <div className="space-y-2">
                                  <div className="font-medium text-sm">
                                    Message Status
                                  </div>
                                  <div className="text-sm text-slate-600">
                                    {thread.user_participants[0]?.first_name}{" "}
                                    {thread.user_participants[0]?.last_name}{" "}
                                    hasn't read your latest message yet.
                                  </div>
                                  {thread.user_participants[0]
                                    ?.last_read_at && (
                                    <div className="text-xs text-slate-600">
                                      Last seen:{" "}
                                      {format(
                                        new Date(
                                          thread.user_participants[0].last_read_at,
                                        ),
                                        "MMM d, h:mm a",
                                      )}
                                    </div>
                                  )}
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Specialty/Role */}
                  {thread.user_participants.length > 0 &&
                    thread.user_participants[0]?.specialty_name && (
                      <div className="flex min-w-0 items-center gap-1.5">
                        <div
                          className={`
                        h-2 w-2 shrink-0 rounded-full
                        ${isSelected ? "bg-primary" : "bg-slate-300"}
                      `}
                        />
                        <p
                          title={thread.user_participants[0].specialty_name}
                          className={`
                        min-w-0 truncate text-sm font-medium leading-5
                        ${
                          isSelected
                            ? "text-primary"
                            : "text-slate-500 group-hover:text-slate-600"
                        }
                      `}
                        >
                          {thread.user_participants[0].specialty_name}
                        </p>
                      </div>
                    )}

                  {/* Assigned User */}
                  {(() => {
                    const p = thread.user_participants[0];
                    if (!p) {
                      return null;
                    }
                    const au =
                      "assignedUser" in p
                        ? (
                            p as {
                              assignedUser?: {
                                firstName: string | null;
                                lastName: string | null;
                              } | null;
                            }
                          ).assignedUser
                        : null;
                    if (!au) {
                      return null;
                    }
                    return (
                      <div className="flex min-w-0 items-center gap-1.5">
                        <UserCircle
                          className={`h-3 w-3 shrink-0 ${isSelected ? "text-primary" : "text-slate-500"}`}
                        />
                        <p
                          title={[au.firstName, au.lastName]
                            .filter(Boolean)
                            .join(" ")}
                          className={`min-w-0 truncate text-xs leading-5 ${isSelected ? "text-primary" : "text-slate-500"}`}
                        >
                          {au.firstName} {au.lastName}
                        </p>
                      </div>
                    );
                  })()}

                  {/* Last Message Preview */}
                  <div className="flex min-w-0 items-center gap-2 pt-1">
                    <MessageIcon
                      className={`
                      w-4 h-4 flex-shrink-0
                      ${
                        isSelected
                          ? "text-primary"
                          : "text-slate-400 group-hover:text-slate-500"
                      }
                    `}
                    />
                    <p
                      className={`
                      min-w-0 flex-1 truncate text-sm leading-5
                      ${thread.last_message?.type === "job-offer" || thread.last_message?.type === "job-offer-proposal" || thread.last_message?.type === "job-offer-shifts-canceled" || thread.last_message?.type === "credential-edit-request" ? "font-medium" : ""}
                      ${
                        isSelected
                          ? "text-slate-700"
                          : "text-slate-600 group-hover:text-slate-700"
                      }
                    `}
                    >
                      {lastMessage.text}
                    </p>
                    {/* Unread Badge */}
                    {thread.unread_count > 0 && (
                      <Badge
                        size="small"
                        className="h-5 min-w-5 shrink-0 justify-center bg-primary px-1.5 py-0 text-xs font-semibold text-primary-foreground"
                      >
                        {thread.unread_count > 99 ? "99+" : thread.unread_count}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </button>
          );
        })}

        {/* Load More Trigger & Loading State */}
        {hasMore && (
          <div
            ref={loadMoreRef}
            className="flex items-center justify-center py-4"
          >
            {isLoading ? (
              <div className="flex items-center space-x-2 text-slate-500">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-300 border-t-slate-600"></div>
                <span className="text-sm">Loading conversations...</span>
              </div>
            ) : (
              <div className="h-4" /> // Invisible trigger for intersection observer
            )}
          </div>
        )}

        {/* {!hasMore && threads.length > 0 && (
          <div className="flex items-center justify-center py-4">
            <p className="text-slate-400 text-sm">No more conversations to load</p>
          </div>
        )} */}
      </div>
    </div>
  );
};

export const ChatThreadList = memo(ChatThreadListComponent);
